import { useEffect, useState } from "react";
import { Users2, ListChecks, ChefHat, FileText, Wallet, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Panel, money } from "./saUi";

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
    <div className={`w-9 h-9 rounded-lg grid place-items-center mb-3 ${accent}`}><Icon className="w-5 h-5" strokeWidth={2.5} /></div>
    <p className="text-3xl font-head font-black leading-none">{value}</p>
    <p className="text-xs text-slate-500 mt-1">{label}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
  </div>
);

function RevenueChart({ series, currency }) {
  if (!series?.length) return null;
  const W = 720, H = 160, P = 8;
  const max = Math.max(...series.map((d) => d.amount), 1);
  const bw = (W - P * 2) / series.length;
  const total = series.reduce((s, d) => s + d.amount, 0);
  const pts = series.map((d, i) => {
    const x = P + i * bw + bw / 2;
    const y = H - P - (d.amount / max) * (H - P * 2);
    return `${x},${y}`;
  }).join(" ");
  const last = series[series.length - 1];
  return (
    <Panel title="Omzet — laatste 30 dagen" data-testid="sa-revenue-chart">
      <div className="flex items-end justify-between mb-3">
        <div>
          <p className="text-2xl font-head font-black text-emerald-600">{money(total, currency)}</p>
          <p className="text-xs text-slate-400">totaal deze periode</p>
        </div>
        <p className="text-xs text-slate-400">vandaag: <b className="text-slate-600">{money(last.amount, currency)}</b></p>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" preserveAspectRatio="none">
        <defs>
          <linearGradient id="revfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#818cf8" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#818cf8" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polyline points={`${P},${H - P} ${pts} ${W - P},${H - P}`} fill="url(#revfill)" stroke="none" />
        <polyline points={pts} fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1">
        <span>{new Date(series[0].date).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}</span>
        <span>{new Date(last.date).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}</span>
      </div>
    </Panel>
  );
}

export default function Metrics() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then((r) => setM(r.data)).catch(() => {}); }, []);
  if (!m) return <div className="flex items-center gap-2 text-slate-400 py-12"><Loader2 className="w-5 h-5 animate-spin" /> Laden…</div>;

  const cc = m.content_counts, sc = m.status_counts, rc = m.role_counts;
  return (
    <div className="space-y-6" data-testid="sa-metrics">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users2} label="Gebruikers" value={m.total_users} sub={`+${m.new_users_month ?? 0} deze maand`} accent="bg-indigo-100 text-indigo-600" />
        <StatCard icon={Wallet} label="Omzet (betaald)" value={money(m.revenue.total, m.revenue.currency)} sub={`${m.revenue.paid_count} van ${m.revenue.txn_count} transacties`} accent="bg-emerald-100 text-emerald-600" />
        <StatCard icon={ListChecks} label="Taken" value={cc.tasks} sub={`${cc.groups} groepen · ${cc.polls} polls`} accent="bg-amber-100 text-amber-600" />
        <StatCard icon={ChefHat} label="Recepten" value={cc.recipes} sub={`${cc.content} content-items`} accent="bg-teal-100 text-teal-600" />
      </div>

      <RevenueChart series={m.revenue.series_30d} currency={m.revenue.currency} />

      <Panel title="Abonnementsstatus (gebruikers)">
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
          {["proef", "actief", "verlopen", "vrij", "friends_family", "beheer"].map((k) => (
            <div key={k} className="bg-slate-50 border border-slate-100 rounded-xl p-4 text-center">
              <p className="text-2xl font-head font-black">{sc[k] || 0}</p>
              <p className="text-xs text-slate-500 capitalize mt-0.5">{k === "friends_family" ? "Friends & Family" : k}</p>
            </div>
          ))}
        </div>
        {m.paywall_enabled === false && (
          <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <FileText className="w-3.5 h-3.5 inline mr-1" /> Paywall staat <b>uit</b> — alle gebruikers hebben onbeperkte toegang (preview/instellingen).
          </p>
        )}
      </Panel>
    </div>
  );
}
